/* Node size comes from computed layout constants, applied via inline style. */
/* eslint-disable react/forbid-dom-props */
import { Handle, type Node, type NodeProps, Position } from "@xyflow/react";
import React from "react";

import { getInitials } from "./nodeInitials";
import { NODE_HEIGHT, NODE_WIDTH } from "./treeGraph.constants";
import type { EntityNodeData } from "./treeGraph.types";

import styles from "./GraphNodeItem.module.css";

/**
 * Shape class per entity type: portrait card for persons, circle for objects, drop pin for places,
 * hexagon for creatures, shield for organizations, diamond for events, octagon for species,
 * star for concepts, trapezoid for custom nodes.
 */
const SHAPE_CLASSES: Record<string, string> = {
	Object: styles.graphShapeCircle,
	Place: styles.graphShapePin,
	Creature: styles.graphShapeHex,
	Organization: styles.graphShapeShield,
	Event: styles.graphShapeDiamond,
	Species: styles.graphShapeOctagon,
	Concept: styles.graphShapeStar,
	Custom: styles.graphShapeTrapezoid
};

const getShapeClass = (type: string): string => SHAPE_CLASSES[type] ?? styles.graphShapeTrapezoid;

/**
 * React Flow custom node rendering one tree entity. `draggable` comes from the canvas: it is on in
 * edit mode and off in view mode, and the node shows a grab or a plain pointer cursor to match, so
 * a read-only canvas never offers a move it will not honour.
 */
const GraphNodeItem = ({ data, draggable }: NodeProps<Node<EntityNodeData>>) => {
	const { entity, isSelected } = data;
	const image = entity.imageUrl ? (
		<img alt="" draggable={false} src={entity.imageUrl} />
	) : (
		<span className={styles.graphNodeInitials}>{getInitials(entity.title)}</span>
	);

	const content =
		entity.type === "Person" ? (
			<span className={styles.graphCard}>
				<span className={styles.graphCardImage}>{image}</span>
				<span className={styles.graphNodeTitle}>{entity.title}</span>
				{entity.personTitle && <span className={styles.graphNodeSubtitle}>{entity.personTitle}</span>}
			</span>
		) : (
			<>
				<span className={getShapeClass(entity.type)}>
					<span className={styles.graphShapeInner}>{image}</span>
				</span>
				{/* Custom has no built-in silhouette to identify it, so its name carries the card
				    backing a person's name gets, rather than sitting bare on the canvas. */}
				<span
					className={
						entity.type === "Custom"
							? `${styles.graphNodeTitle} ${styles.graphNodeTitleCard}`
							: styles.graphNodeTitle
					}>
					{entity.title}
				</span>
			</>
		);

	const className = [
		styles.graphNode,
		draggable ? styles.graphNodeDraggable : "",
		isSelected ? styles.graphNodeSelected : ""
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div className={className} style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}>
			<Handle className={styles.hiddenHandle} id="t" position={Position.Top} type="target" />
			<Handle className={styles.hiddenHandle} id="l" position={Position.Left} type="target" />
			{content}
			<Handle className={styles.hiddenHandle} id="b" position={Position.Bottom} type="source" />
			<Handle className={styles.hiddenHandle} id="r" position={Position.Right} type="source" />
		</div>
	);
};

export default GraphNodeItem;
